import React, { useState, useEffect, useRef } from "react";
import { useAccess } from "../../../hooks/useAccess";
import { logEvent, CUSTOMER_EVENTS } from "../../../utils/amplitude";
import AppLoader from "../../../components/loader";
import { useFetchCustomers } from "../../../apolloClient/hooks/general/fetchHook";
import { Customer } from "../../../redux/types";
import { useNavigate } from "react-router-dom";
import { TxtInput } from "../../../components";
import moment from "moment";
import { Box } from "@mui/material";
import searchIcon from "../../../images/searchIcon.png";

import {
  PageContainer,
  ContentWrapper,
  HeaderSection,
  TitleRow,
  PageTitle,
  PageSubtitle,
  SearchInputBox,
  SearchIcon,
  TableWrapper,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  PrimaryCell,
  SecondaryText,
  PaginationWrapper,
  PaginationText,
  EmptyState,
  EmptyStateText,
} from "../../../components/table/CommonTable.styled";

interface CustomersProps {}

export const Customers: React.FC<CustomersProps> = () => {
  const { loading: accessLoading } = useAccess();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [keyword, setKeyword] = useState("");

  const limit = 500; // Fetch more at once
  const offset = 0;

  const { loading, error, customersData } = useFetchCustomers({
    limit,
    offset,
  });

  // Track customers page viewed
  useEffect(() => {
    if (customersData && !loading) {
      logEvent(CUSTOMER_EVENTS.CUSTOMERS_PAGE_VIEWED, { 
        customer_count: customersData.length 
      });
    }
  }, [customersData, loading]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    const totalPages = Math.ceil(filterCustomers().length / rowsPerPage);
    setPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
  };

  const handleViewCustomer = (customerId: number) => {
    navigate(`/customers/${customerId}`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = moment(dateString);
    if (!date.isValid()) return 'N/A';
    return date.format('MM/DD/YYYY');
  };

  console.log("customersData:", customersData);

  const filterCustomers = () => {
    if (!customersData) return [];
    if (keyword.length > 0) {
      return customersData.filter((customer: Customer) => {
        const firstName = customer.firstName?.toLowerCase() || "";
        const lastName = customer.lastName?.toLowerCase() || "";
        const fullName = `${firstName} ${lastName}`;
        const email = customer.email?.toLowerCase() || "";
        const phone = customer.phone || "";
        const searchKeyword = keyword.toLowerCase();

        return (
          firstName.includes(searchKeyword) ||
          lastName.includes(searchKeyword) ||
          fullName.includes(searchKeyword) ||
          email.includes(searchKeyword) ||
          phone.includes(searchKeyword) ||
          customer.id?.toString().includes(searchKeyword)
        );
      });
    }
    return customersData;
  };

  if (accessLoading || loading) {
    return <AppLoader isLoading={accessLoading || loading} />;
  }

  if (error) {
    return (
      <PageContainer>
        <ContentWrapper>
          <EmptyState>
            <EmptyStateText>Error loading customers: {error.message}</EmptyStateText>
          </EmptyState>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const filteredCustomers = filterCustomers();
  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.ceil(filteredCustomers.length / rowsPerPage);

  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderSection>
          <TitleRow>
            <PageTitle>Customers</PageTitle>
          </TitleRow>
          <PageSubtitle>{filteredCustomers.length} total customers</PageSubtitle>
        </HeaderSection>

        <Box sx={{ mb: 3 }}>
          <SearchInputBox>
            <SearchIcon
              src={searchIcon}
              style={{ margin: "0 12px" }}
              onClick={() => inputRef.current?.focus()}
            />
            <TxtInput
              placeholder="Search by name, email or phone..."
              onChange={setKeyword}
              inputRef={inputRef}
              background="white"
              width="100%"
            />
          </SearchInputBox>
        </Box>

        <TableWrapper>
          <TableHeader>
            <TableHeaderCell width="10%">ID</TableHeaderCell>
            <TableHeaderCell width="25%">Name</TableHeaderCell>
            <TableHeaderCell width="25%">Email</TableHeaderCell>
            <TableHeaderCell width="20%">Phone</TableHeaderCell>
            <TableHeaderCell width="15%">Created</TableHeaderCell>
            <TableHeaderCell width="5%"></TableHeaderCell>
          </TableHeader>

          {filteredCustomers.length === 0 ? (
            <EmptyState>
              <EmptyStateText>
                {keyword ? "No customers found matching your search" : "No customers found"}
              </EmptyStateText>
            </EmptyState>
          ) : (
            paginatedCustomers.map((customer: Customer) => {
              const fullName =
                customer.firstName && customer.lastName
                  ? `${customer.firstName} ${customer.lastName}`
                  : customer.firstName || customer.lastName || "N/A";

              return (
                <TableRow
                  key={customer.id}
                  onClick={() => handleViewCustomer(customer.id)}
                >
                  <TableCell width="10%" mobileLabel="ID">
                    <SecondaryText>#{customer.id}</SecondaryText>
                  </TableCell>
                  <PrimaryCell width="25%" mobileLabel="Name">
                    {fullName}
                  </PrimaryCell>
                  <TableCell width="25%" mobileLabel="Email">
                    {customer.email || "-"}
                  </TableCell>
                  <TableCell width="20%" mobileLabel="Phone">
                    {customer.phone || "-"}
                  </TableCell>
                  <TableCell width="15%" mobileLabel="Created">
                    <SecondaryText>{formatDate(customer.createdAt)}</SecondaryText>
                  </TableCell>
                  <TableCell width="5%" style={{ textAlign: "right" }}>
                    <Box
                      component="span"
                      sx={{
                        color: "#4b87ed",
                        fontSize: 13,
                        fontWeight: 500,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      View →
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}

          {filteredCustomers.length > 0 && (
            <PaginationWrapper>
              <Box
                component="button"
                onClick={goToPreviousPage}
                disabled={page === 0}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
                  color: page === 0 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page === 0 ? "not-allowed" : "pointer",
                  "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                ← Prev
              </Box>
              <PaginationText>
                Page {page + 1} of {totalPages}
              </PaginationText>
              <Box
                component="button"
                onClick={goToNextPage}
                disabled={page >= totalPages - 1}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page >= totalPages - 1 ? "#f3f4f6" : "#e5e7eb",
                  color: page >= totalPages - 1 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                  "&:hover": page >= totalPages - 1 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                Next →
              </Box>
            </PaginationWrapper>
          )}
        </TableWrapper>
      </ContentWrapper>
    </PageContainer>
  );
};